/**
 * Estilos (cores) dos módulos de permissão para a UI.
 *
 * A CLASSIFICAÇÃO (que permissão cai em qual módulo), a ordem e os labels são
 * compartilhados via `@workflow/types` (`permissao-modulo.type`) — fonte única
 * entre front e back, para o filtro server-side bater com o agrupamento visual.
 * Aqui mora apenas o que é específico do front: as classes Tailwind.
 */
import {
  PermissaoModulo,
  PERMISSAO_MODULO_ORDER,
  PERMISSAO_MODULO_LABEL,
  getPermissaoModulo,
} from "@workflow/types";

export { PERMISSAO_MODULO_ORDER, getPermissaoModulo };
export type { PermissaoModulo };

export interface PermissaoModuloStyle {
  label: string;
  /** Classes da pílula (borda + fundo + texto), claro e escuro. */
  chip: string;
  /** Cor da "bolinha" de status. */
  dot: string;
  /** Realce de texto para cabeçalho de grupo. */
  text: string;
}

type PermissaoModuloCor = Omit<PermissaoModuloStyle, "label">;

// Matizes bem separados no círculo cromático para diferenciar os módulos à
// primeira vista: azul, violeta, âmbar, esmeralda, rosa, lima, ciano, vermelho
// e cinza (neutro).
const PERMISSAO_MODULO_COR: Record<PermissaoModulo, PermissaoModuloCor> = {
  ATENDIMENTO: {
    chip: "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    dot: "bg-blue-500",
    text: "text-blue-700 dark:text-blue-300",
  },
  PRE_PRODUCAO: {
    chip: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
    dot: "bg-violet-500",
    text: "text-violet-700 dark:text-violet-300",
  },
  COMPRAS: {
    chip: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-300",
  },
  PRODUCAO: {
    chip: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    dot: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-300",
  },
  CRIACAO: {
    chip: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-700 dark:bg-fuchsia-500/15 dark:text-fuchsia-300",
    dot: "bg-fuchsia-500",
    text: "text-fuchsia-700 dark:text-fuchsia-300",
  },
  FINANCEIRO: {
    chip: "border-pink-500/30 bg-pink-500/10 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300",
    dot: "bg-pink-500",
    text: "text-pink-700 dark:text-pink-300",
  },
  PLANEJAMENTO: {
    chip: "border-lime-500/30 bg-lime-500/10 text-lime-700 dark:bg-lime-500/15 dark:text-lime-300",
    dot: "bg-lime-500",
    text: "text-lime-700 dark:text-lime-300",
  },
  TECNOLOGIA: {
    chip: "border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300",
    dot: "bg-cyan-500",
    text: "text-cyan-700 dark:text-cyan-300",
  },
  ACESSO: {
    chip: "border-red-500/30 bg-red-500/10 text-red-700 dark:bg-red-500/15 dark:text-red-300",
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-300",
  },
  GERAL: {
    chip: "border-slate-400/30 bg-slate-400/10 text-slate-600 dark:bg-slate-400/15 dark:text-slate-300",
    dot: "bg-slate-400",
    text: "text-slate-600 dark:text-slate-300",
  },
};

export const PERMISSAO_MODULO_STYLE: Record<PermissaoModulo, PermissaoModuloStyle> =
  Object.fromEntries(
    PERMISSAO_MODULO_ORDER.map((m) => [
      m,
      { label: PERMISSAO_MODULO_LABEL[m], ...PERMISSAO_MODULO_COR[m] },
    ]),
  ) as Record<PermissaoModulo, PermissaoModuloStyle>;

export const getPermissaoModuloStyle = (nome: string): PermissaoModuloStyle =>
  PERMISSAO_MODULO_STYLE[getPermissaoModulo(nome)];
